/**
 * AgentWindowContent - Renders the real AgentDetailPage inside a tiled window.
 *
 * Wraps AgentDetailPage in CapabilityDetailProvider (which the route-level
 * CapabilityDetailPage normally provides) and passes appKey/name as props
 * so the page works without route params.
 *
 * AgentDetailPage itself pushes the action header layer via the headerLayerId
 * prop — it owns its own save/publish/edit logic (customSave). This component
 * only provides the CapabilityDetailProvider shell and the archive dialog.
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CapabilityDetailProvider } from '@/lib/capability-detail-context';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';
import { Archive } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AgentDetailPage from '@/pages/agents/agent-detail-page';
import { useTiledWindows } from '@/components/tiled-windows';
import type { AgentWindow } from '@/types/tiled-window';

interface AgentWindowContentProps {
    window: AgentWindow;
}

/**
 * Parse an entity reference (entityId) into appKey and capability name.
 *
 * entityId may be:
 *   - "appKey/capName"  (cross-app FQCN, possibly with "::method" suffix)
 *   - "capName"         (same-app reference)
 */
function parseEntityRef(entityId: string | number, fallbackAppKey: string): { appKey: string; name: string } {
    const raw = String(entityId).replace(/::.*$/, '');
    if (raw.includes('/')) {
        const slashIdx = raw.indexOf('/');
        return { appKey: raw.substring(0, slashIdx), name: raw.substring(slashIdx + 1) };
    }
    return { appKey: fallbackAppKey, name: raw };
}

export function AgentWindowContent({ window: win }: AgentWindowContentProps) {
    const { appKey, name } = parseEntityRef(win.entityId, win.appKey ?? '');
    const headerLayerId = `window-${win.id}`;
    const queryClient = useQueryClient();
    const { closeWindow } = useTiledWindows();

    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const openArchiveDialog = useCallback(() => setArchiveDialogOpen(true), []);

    const handleArchive = useCallback(async () => {
        if (!appKey || !name) return;
        setIsArchiving(true);
        try {
            await api.delete(`/applications/${appKey}/capabilities/${encodeURIComponent(name)}`);
            toast.success('Agent archived successfully');
            queryClient.invalidateQueries({ queryKey: ['capabilities'] });
            closeWindow(win.id);
        } catch {
            toast.error('Failed to archive agent');
        } finally {
            setIsArchiving(false);
            setArchiveDialogOpen(false);
        }
    }, [appKey, name, queryClient, closeWindow, win.id]);

    return (
        <CapabilityDetailProvider openArchiveDialog={openArchiveDialog}>
            <div className="h-full w-full overflow-auto">
                <AgentDetailPage appKey={appKey} name={name} headerLayerId={headerLayerId} />
            </div>

            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Archive size={18} className="text-destructive" />
                            Archive Agent
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to archive &quot;{name}&quot;? It will no longer
                            appear in the active agent list.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                void handleArchive();
                            }}
                            disabled={isArchiving}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isArchiving ? 'Archiving...' : 'Archive'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </CapabilityDetailProvider>
    );
}
